/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/10/2024 - 13:37:00
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/10/2024
    * - Author          : 
    * - Modification    : 
**/
import axios from "axios";

import { auth } from "../firebase";

export const apis = {
  getElevation: async (lat: number, long: number) => {
    try {
      const response = await axios.get(
        `https://backend.pv-glarecheck.com/elevation/?lat=${lat}&long=${long}`
      );
      // console.log("getElevationresponse",response)
      return response;
    } catch (error) {
      throw error;
    }
  },
};






// Base API URL
const API_BASE_URL = "https://backend.pv-glarecheck.com";

// Create an axios instance for authenticated requests
const authenticatedAxios = axios.create({
  baseURL: API_BASE_URL,
});

// Token storage in localStorage
const TOKEN_KEY = "api_access_token";
const TOKEN_EXPIRY_KEY = "api_token_expiry";
const USER_ID_KEY = "api_user_id";

// Function to get Firebase ID token
const getFirebaseToken = async () => {
  if (!auth.currentUser) {
    throw new Error("No user logged in");
  }
  return await auth.currentUser.getIdToken();
};

// Function to exchange Firebase token for API token
const exchangeToken = async (firebaseToken: any) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/token`, {
      firebase_token: firebaseToken,
    });
    
    // Store the token information
    localStorage.setItem(TOKEN_KEY, response.data.access_token);
    localStorage.setItem(
      TOKEN_EXPIRY_KEY, 
      (Date.now() + response.data.expires_in * 1000).toString()
    );
    localStorage.setItem(USER_ID_KEY, response.data.user_id);
    
    return response.data.access_token;
  } catch (error) {
    console.error("Error exchanging token:", error);
    throw error;
  }
};

// Function to get a valid API token
const getApiToken = async () => {
  // Check if we have a valid token
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  
  // If token exists and is not expired, return it
  if (token && expiry && Date.now() < parseInt(expiry)) {
    return token;
  }
  
  // Otherwise, get a new token
  const firebaseToken = await getFirebaseToken();
  return await exchangeToken(firebaseToken);
};

// Setup axios request interceptor to add Authorization header
authenticatedAxios.interceptors.request.use(
  async (config) => {
    try {
      const token = await getApiToken();
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      console.error("Authentication error:", error);
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses by clearing token storage
authenticatedAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear stored tokens on authentication failure
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      localStorage.removeItem(USER_ID_KEY);
      // You could redirect to login page here if needed
    }
    return Promise.reject(error);
  }
);

// Export the API functions
export const api = {
  // Get azimuth data
  getAzimuth: async (data: any) => {
    try {
      const response = await authenticatedAxios.put(`/azimuth`, data);
      return response.data;
    } catch (error) {
      console.error("Error getting azimuth:", error);
      throw error;
    }
  },
  
  // Get elevation data
  getElevation: async (lat: any, long: any) => {
    try {
      const response = await authenticatedAxios.get(
        `/elevation/?lat=${lat}&long=${long}`
      );
      return response.data;
    } catch (error) {
      console.error("Error getting elevation:", error);
      throw error;
    }
  },
  
  // Generate reports
  generateReports: async (data: any) => {
    try {
      const response = await authenticatedAxios.post(`/getPDF`, data);
      return response.data;
    } catch (error) {
      console.error("Error generating reports:", error);
      throw error;
    }
  },
  
  // Check PDF processing status
  checkPdfProcessing: async (identifier: any) => {
    try {
      const response = await authenticatedAxios.get(
        `/check-pdf-processing?identifier=${identifier}`
      );
      return response.data;
    } catch (error) {
      console.error("Error checking PDF processing:", error);
      throw error;
    }
  },
  
  // Get current user's API token (for debugging/display)
  getCurrentToken: async () => {
    try {
      return await getApiToken();
    } catch (error) {
      console.error("Error getting API token:", error);
      throw error;
    }
  },
};